import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/** Add JD1 selections through the Horizon 4 cart event contract, including rendered sections. */
export async function addItems(target, items, source = 'jd1-boutique') {
  if (!Array.isArray(items) || !items.length) throw new Error('Selecciona un producto.');
  const deferred = CartLinesUpdateEvent.createPromise();
  // The caller receives errors too; guard the event promise when no cart listener is mounted.
  deferred.promise.catch(() => {});
  target.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add', context: 'product',
    lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity || 1 })),
    promise: deferred.promise,
  }));
  const root = window.Shopify?.routes?.root || '/';
  const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((element) => element.dataset.sectionId).filter(Boolean))].slice(0, 5);
  try {
    const response = await fetch(window.Theme?.routes?.cart_add_url || `${root}cart/add.js`, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sections.join(','), sections_url: window.location.pathname }),
    });
    const added = await response.json();
    if (!response.ok || added.status) throw new Error(added.description || added.message || 'No se pudo agregar al carrito.');
    const cartResponse = await fetch(`${root}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!cartResponse.ok) throw new Error('El producto se agregó, pero no se pudo actualizar el carrito. Recarga la página.');
    const cart = await cartResponse.json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source, sourceId: target.id || source, itemCount: items.reduce((n, item) => n + (item.quantity || 1), 0), sections: added.sections, didError: false },
    });
    return { cart, added };
  } catch (error) {
    deferred.reject(error);
    target.dispatchEvent(new CartErrorEvent({ error: error.message, code: 'INVALID' }));
    throw error;
  }
}

// JD1 presentation blocks consume the settled HTML. Native components keep StandardEvents.
document.addEventListener(StandardEvents.productSelect, (event) => {
  const origin = event.target;
  const scope = origin instanceof Element ? origin.closest('product-component, product-card') : null;
  event.promise?.then(({ detail }) => {
    if (!detail || detail.didError || !origin?.isConnected) return;
    origin.dispatchEvent(new CustomEvent('jd1:variant-update', {
      bubbles: true,
      detail: { resource: detail.resource, sourceId: detail.sourceId, data: detail, scope },
    }));
  }).catch((error) => { if (error?.name !== 'AbortError') console.warn('[jd1-product]', error); });
});

// Optional header total is independent of the drawer section in Horizon 4.
let headerTotalRequest;
for (const name of [StandardEvents.cartLinesUpdate, StandardEvents.cartDiscountUpdate]) {
  document.addEventListener(name, (event) => {
    if (!document.querySelector('[data-pf-cart-total]')) return;
    event.promise?.then(async ({ detail }) => {
      if (detail?.didError) return;
      headerTotalRequest?.abort();
      headerTotalRequest = new AbortController();
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { signal: headerTotalRequest.signal });
      if (!response.ok) return;
      const cart = await response.json();
      const formatted = new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency: cart.currency }).format(cart.total_price / 100);
      document.querySelectorAll('[data-pf-cart-total]').forEach((element) => {
        element.textContent = formatted;
        element.hidden = !cart.item_count;
      });
    }).catch((error) => { if (error?.name !== 'AbortError') console.warn('[jd1-cart-total]', error); });
  });
}
